import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

const CSRF_HEADER = 'X-CSRF-Token'
const CSRF_COOKIE = 'cv_csrf'

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

export const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

// Attach CSRF on unsafe methods.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method || 'get').toUpperCase()
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = readCookie(CSRF_COOKIE)
    if (token) {
      config.headers.set(CSRF_HEADER, token)
    }
  }
  return config
})

// Single-flight refresh logic.
let refreshPromise: Promise<void> | null = null

async function performRefresh(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post('/api/v1/auth/refresh', null, { withCredentials: true })
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const status = error.response?.status

    if (status === 401 && original && !original._retry) {
      const url = original.url || ''
      const isAuthEndpoint =
        url.includes('/auth/login') ||
        url.includes('/auth/refresh') ||
        url.includes('/auth/logout')
      if (isAuthEndpoint) {
        return Promise.reject(error)
      }
      original._retry = true
      try {
        await performRefresh()
        return api.request(original)
      } catch (refreshErr) {
        window.dispatchEvent(new CustomEvent('cafe:session-expired'))
        return Promise.reject(refreshErr)
      }
    }

    return Promise.reject(error)
  },
)

export function extractError(error: unknown, fallback = 'Ocurrió un error'): string {
  const ax = error as AxiosError<{ detail?: string }>
  if (ax?.response?.data?.detail) return ax.response.data.detail
  if (ax?.message) return ax.message
  return fallback
}
